import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import _ from "lodash";
import cx from "classnames";

import styles from "./createWorkspace.module.scss";
import { Dropdown, Form, Grid, Modal } from "semantic-ui-react";

import SaveChanges from "components/ui/Buttons/SaveButton";
import Cancel from "components/ui/Buttons/CancelButton";
import {createCleverstoryWorkspace,updateCleverstoryWorkspace} from "actions/cleverstory";

const initWorkspace = {
  name: "",
  description: "",
  user: null,
};

const initErrorFields = [
  {
    name: "user",
    title: "User",
    error: false,
    message: null,
  },
  {
    name: "name",
    title: "Name",
    error: false,
    message: null,
  },
  {
    name: "description",
    title: "Description",
    error: false,
    message: null,
  },
];

export default function CreateWorkspace({ account, workspace, open, setOpen }) {
  const dispatch = useDispatch();

  const [initialRendering, setInitialRendering] = useState(true);
  const [workspaceDetail, setWorkspaceDetail] = useState(initWorkspace);
  const [error, setError] = useState(null);
  const [errorFields, setErrorFields] = useState(initErrorFields);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const creatingWorkspace = useSelector((state) =>
    _.get(state, "cleverstoryWorkspace.createWorkspace")
  );
  const updatingWorkspace = useSelector((state) =>
    _.get(state, "cleverstoryWorkspace.updateWorkspace")
  );
  const users = useSelector((state) => _.get(state, "users.items", []));
  const usersOptions = (users || []).map((item) => {
    return {
      key: item.id,
      text: item.name,
      value: item.id,
    };
  });

  useEffect(() => {
    setWorkspaceDetail(workspace ? {...workspace} : { ...initWorkspace });
    setErrorFields([...initErrorFields]);
    setError(null);
    setFormSubmitted(false);
  }, [open]);

  useEffect(() => {
    validateFields();

    if (isFormSubmitted) {
      // reset form if user trying to resets the value
      setFormSubmitted(false);
    }
  }, [workspaceDetail]);

  useEffect(() => {
    if (!initialRendering && creatingWorkspace && !creatingWorkspace.creating) {
      if (creatingWorkspace.success) {
        setOpen(false);
        toast.success(`${workspaceDetail.name} created successfully`, {
          toastId: "created"
        });
      } else if (creatingWorkspace.error) {
        setError(creatingWorkspace.error);
      }
    }
  }, [creatingWorkspace]);

  useEffect(() => {
    if (!initialRendering && updatingWorkspace && !updatingWorkspace.updating) {
      if (updatingWorkspace.success) {
        setOpen(false);
        toast.success(`${workspaceDetail.name} updated successfully`, {
          toastId: "updated"
        });
      } else if (updatingWorkspace.error) {
        setError(updatingWorkspace.error);
      }
    }
  }, [updatingWorkspace]);

  useEffect(() => {
    let message = null;
    for (var i = 0; i < errorFields.length; i++) {
      if (errorFields[i].error) {
        message = errorFields[i].message;
        break;
      }
    }

    setError(message);
  }, [errorFields]);

  setTimeout(() => {
    setInitialRendering(false);
  }, 3000);

  const isFieldError = (fieldName) => {
    let error = false;
    if (isFormSubmitted) {
      errorFields.forEach((field, idx) => {
        if (field.name === fieldName && field.error) {
          error = true;
        }
      });
    }

    return error;
  };

  const isFormValid = () => {
    let errors = errorFields.filter((item) => item.error);
    if (errors.length > 0) return false;

    return true;
  };

  const validateFields = () => {
    let fields = [];
    for (var i = 0; i < errorFields.length; i++) {
      let field = errorFields[i].name;
      let value = workspaceDetail[field];

      errorFields[i].error =
        value === null || value === undefined ? true : false;

      if (!value) {
          errorFields[i].error = true;
          errorFields[i].message = errorFields[i].title + " is required";
          
          if(workspace) {
            errorFields[0].error = false;
            errorFields[0].message = null;
          }

      } else {
        errorFields[i].error = false;
        errorFields[i].message = null;
      }
      fields.push(errorFields[i]);
    }
    setErrorFields(fields);
  };

  const onCreateWorkspace = (e) => {
    e.preventDefault();

    if (e.type === "submit") {
      setFormSubmitted(true);

      if (isFormValid()) {
        dispatch(
          createCleverstoryWorkspace({
            account: account.id,
            user: workspaceDetail.user,
            name: workspaceDetail.name,
            description: workspaceDetail.description,
          })
        );
      }
    }
  };

  const onUpdateWorkspace = (e) => {
    e.preventDefault();

    if (e.type === "submit") {
      setFormSubmitted(true);

      if (isFormValid()) {
        dispatch(
          updateCleverstoryWorkspace({
            id: workspace.id,
            data: {
              name: workspaceDetail.name,
              description: workspaceDetail.description,
            }
          })
        );
      }
    }
  };

  const handleUserChange = (e, data) => {
    let user = data.value;
    setWorkspaceDetail({ ...workspaceDetail, user });
  };

  const renderContent = () => {
    if (workspace) {
      return updatingWorkspace && updatingWorkspace.updating ? "Updating" : "Update";
    }
    return creatingWorkspace && creatingWorkspace.creating ? "Creating" : "Create";
  };

  const renderForm = () => {
    if (workspace !== undefined && workspace !== null) {
      return (
        <Form onSubmit={onUpdateWorkspace}>
          <Grid>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <span className={cx(styles.title)}>
                  Update Workspace
                </span>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <Form.Input
                  fluid
                  id="name"
                  label={"Name"}
                  placeholder="Workspace Name"
                  value={workspaceDetail.name}
                  onChange={(e) =>
                    setWorkspaceDetail({
                      ...workspaceDetail,
                      name: e.target.value,
                    })
                  }
                  error={isFieldError("name")}
                ></Form.Input>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column>
                <Form.TextArea
                  fluid
                  id="description"
                  className={styles.description}
                  label={"Description"}
                  placeholder="Workspace Description"
                  value={workspaceDetail.description}
                  onChange={(e) =>
                    setWorkspaceDetail({
                      ...workspaceDetail,
                      description: e.target.value,
                    })
                  }
                  error={isFieldError("description")}
                ></Form.TextArea>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                {isFormSubmitted && (
                  <div className={cx(styles.error)}> {error}</div>
                )}
              </Grid.Column>
              <Grid.Column textAlign="right">
                <SaveChanges content={renderContent()} type="submit" />
                <Cancel content="Cancel" onClick={() => setOpen(false)} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      );
    }
    return (
      <Form onSubmit={onCreateWorkspace}>
        <Grid>
          <Grid.Row>
            <Grid.Column textAlign="center">
              <span className={cx(styles.title)}>
                Create Workspace
              </span>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <Dropdown
                placeholder="Select Users"
                fluid
                selection
                options={usersOptions}
                value={workspaceDetail.user}
                onChange={handleUserChange}
                error={isFieldError("user")}
              />
            </Grid.Column>
          </Grid.Row>

          {workspaceDetail.user && (
            <>
              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    fluid
                    id="name"
                    label={"Name"}
                    placeholder="Workspace Name"
                    value={workspaceDetail.name}
                    onChange={(e) =>
                      setWorkspaceDetail({
                        ...workspaceDetail,
                        name: e.target.value,
                      })
                    }
                    error={isFieldError("name")}
                  ></Form.Input>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Form.TextArea
                    fluid
                    id="description"
                    className={styles.description}
                    label={"Description"}
                    placeholder="Workspace Description"
                    value={workspaceDetail.description}
                    onChange={(e) =>
                      setWorkspaceDetail({
                        ...workspaceDetail,
                        description: e.target.value,
                      })
                    }
                    error={isFieldError("description")}
                  ></Form.TextArea>
                </Grid.Column>
              </Grid.Row>
            </>
          )}
          <Grid.Row columns={2}>
            <Grid.Column>
              {isFormSubmitted && (
                <div className={cx(styles.error)}> {error}</div>
              )}
            </Grid.Column>
            <Grid.Column textAlign="right">
              <SaveChanges content={renderContent()} type="submit" />
              <Cancel content="Cancel" onClick={() => setOpen(false)} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  };

  return (
    <>
      <Modal
        size="tiny"
        open={open}
        centered
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        className={cx(styles.create)}
      >
        <Modal.Content className={cx(styles.content)}>
          {renderForm()}
        </Modal.Content>
      </Modal>
    </>
  );
}
